import {
  type ReactElement,
  type ReactNode,
  type Ref,
  cloneElement,
  isValidElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset as floatingOffset,
  safePolygon,
  shift,
  size as floatingSize,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useTransitionStyles,
} from "@floating-ui/react";

import { cx, styled } from "simplydo/react/styled";

import {
  FocusStackContext,
  createFocusTrap,
  restoreFocus,
  useFocusStack,
} from "../utils/FocusStack";
import { getDefaultMountNode } from "../utils/mountNode";
import { usePrefersReducedMotion } from "../utils/reducedMotion";
import { POPUP_AVAILABLE_WIDTH_VAR, PopupSurface } from "./Popup.styles";
import type { PopupContext, PopupOn, PopupProps } from "./Popup.types";
import { mapPopupPosition } from "./positions";

function normalizeOn(on: PopupProps["on"]): PopupOn[] {
  if (!on) {
    return ["hover"];
  }
  return Array.isArray(on) ? on : [on];
}

function resolveOffset(
  offsetProp: PopupProps["offset"],
): number | { mainAxis: number; crossAxis: number } {
  if (offsetProp == null) {
    return 8;
  }
  if (typeof offsetProp === "number") {
    return offsetProp;
  }
  const [skidding = 0, distance = 8] = offsetProp;
  return { mainAxis: distance ?? 8, crossAxis: skidding };
}

function resolveContextElement(context: PopupContext): Element | null {
  if (context == null) {
    return null;
  }
  if (typeof context === "object" && "current" in context) {
    const current = context.current;
    return current instanceof Element ? current : null;
  }
  return context instanceof Element ? context : null;
}

const PopupComponent = ({
  id,
  trigger,
  children,
  on,
  position,
  offset: offsetProp,
  open: openProp,
  onClose,
  onOpen,
  context: contextProp,
  wide,
  style,
  className,
  transparent,
  fluid,
  hoverable,
  disabled,
  size,
  ref: propsRef,
  trapFocus,
}: PopupProps & { ref?: Ref<Element> }): ReactElement => {
  const focusStack = useFocusStack();
  // FloatingPortal creates its mount node asynchronously; track the floating
  // element so the focus-trap effect re-runs once the surface is actually mounted.
  const [floatingNode, setFloatingNode] = useState<HTMLDivElement | null>(null);
  const [internalOpen, setInternalOpen] = useState(openProp ?? false);
  // Popups opened deliberately (click / keyboard) trap focus by default so
  // keyboard users land inside them; hover- and focus-opened ones don't.
  const [openedByClick, setOpenedByClick] = useState(false);

  const isControlled = openProp !== undefined;
  const isOpen = disabled
    ? false
    : isControlled
      ? Boolean(openProp)
      : internalOpen;

  const events = useMemo(() => normalizeOn(on), [on]);
  const enableHover = events.includes("hover");
  const enableClick = events.includes("click");
  const enableFocus = events.includes("focus");

  const {
    refs,
    floatingStyles,
    context: floatingContext,
  } = useFloating({
    open: isOpen,
    onOpenChange: (next, event, reason) => {
      if (disabled) {
        return;
      }
      if (!next && (event as KeyboardEvent).key === "Enter") {
        return;
      }
      setOpenedByClick(next && reason === "click");
      if (!isControlled) {
        setInternalOpen(next);
      }
      const data = { open: next };
      if (next) {
        onOpen?.(event as never, data);
      } else {
        onClose?.(event as never, data);
      }
    },
    placement: mapPopupPosition(position),
    strategy: "absolute",
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingOffset(resolveOffset(offsetProp)),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      floatingSize({
        padding: 8,
        apply({ availableWidth, elements }) {
          // Expose remaining viewport space to the stylesheet, which caps
          // it with the popup's intended width (200 / 300 / 450px). Setting
          // maxWidth inline here would override that cap.
          elements.floating.style.setProperty(
            POPUP_AVAILABLE_WIDTH_VAR,
            `${Math.max(0, availableWidth)}px`,
          );
        },
      }),
    ],
  });

  const hover = useHover(floatingContext, {
    enabled: enableHover && !disabled,
    handleClose: hoverable ? safePolygon() : null,
    delay: hoverable ? { open: 0, close: 100 } : 0,
  });
  const click = useClick(floatingContext, {
    enabled: enableClick && !disabled,
  });
  const focus = useFocus(floatingContext, {
    enabled: enableFocus && !disabled,
  });
  const dismiss = useDismiss(floatingContext, { enabled: !disabled });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
  ]);

  const childRef = isValidElement(trigger)
    ? ((trigger as ReactElement & { ref?: Ref<Element> }).ref ?? null)
    : null;
  // Floating UI callback refs — standard useMergeRefs / setReference pattern.
  // Merge Linaria's forwarded `ref` so styled(Popup) cannot orphan positioning.
  const referenceRef = useMergeRefs([
    refs.setReference,
    childRef,
    propsRef ?? null,
  ]);
  const setFloatingElement = useCallback((node: HTMLDivElement | null) => {
    setFloatingNode(node);
  }, []);
  const mergedFloatingRef = useMergeRefs([
    refs.setFloating,
    setFloatingElement,
  ]);

  useLayoutEffect(() => {
    const el = resolveContextElement(contextProp);
    // Avoid calling setPositionReference(null) when `context` is unused —
    // that would clear Floating UI's default (follow the trigger reference).
    if (el == null && contextProp == null) {
      return;
    }
    if (trigger != null) {
      // Trigger keeps events via setReference; context only anchors position.
      refs.setPositionReference(el);
      return;
    }
    if (el) {
      refs.setReference(el);
    }
  }, [contextProp, trigger, refs]);

  const shouldTrapFocus = trapFocus ?? openedByClick;

  useLayoutEffect(() => {
    if (!shouldTrapFocus || !isOpen || !floatingNode) {
      return;
    }

    const returnTo = document.activeElement as HTMLElement | null;
    const focusTrap = createFocusTrap(floatingNode);
    focusStack.insertFocusTrap(focusTrap);

    return () => {
      focusStack.removeFocusTrap(focusTrap);
      restoreFocus(returnTo, floatingNode);
    };
  }, [isOpen, floatingNode, focusStack, shouldTrapFocus]);

  let triggerNode: ReactNode = null;
  if (trigger != null) {
    if (isValidElement(trigger)) {
      triggerNode = cloneElement(
        trigger as ReactElement,
        // oxlint-disable react-compiler
        getReferenceProps({ ref: referenceRef }),
      );
    } else {
      triggerNode = (
        <span ref={referenceRef} {...getReferenceProps()}>
          {trigger}
        </span>
      );
    }
  }

  const reducedMotion = usePrefersReducedMotion();
  const { isMounted, styles: transitionStyle } = useTransitionStyles(
    floatingContext,
    {
      duration: reducedMotion ? 0 : 200,
      initial: { opacity: 0, top: "-5px" },
      open: { opacity: 1, top: "0px" },
      close: { opacity: 0, top: "5px" },
    },
  );
  const portalRoot = getDefaultMountNode() ?? undefined;

  return (
    <FocusStackContext.Provider value={focusStack}>
      {triggerNode}
      {children != null ? (
        isMounted ? (
          <FloatingPortal root={portalRoot}>
            <PopupSurface
              id={id}
              $fluid={fluid}
              role="dialog"
              aria-modal={"true"}
              tabIndex={-1}
              className={cx(className)}
              style={{ ...floatingStyles, ...transitionStyle, ...style }}
              $transparent={transparent}
              $wide={wide}
              $size={size}
              {...getFloatingProps()}
              ref={mergedFloatingRef}
            >
              {children}
            </PopupSurface>
          </FloatingPortal>
        ) : null
      ) : null}
    </FocusStackContext.Provider>
  );
};

export const Popup = styled(PopupComponent)``;
